import { EOAvatar, EOIcon, EOModal } from '@nx/ui/eo';
import { useI18n } from '@nx/i18n';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  participantCheckInRequested,
  participantCreateRequested,
  participantsWatchRequested,
} from '../../store/slices/participantsSlice';
export function ParticipantsPage() {
  const { t } = useI18n();
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const people = useAppSelector((s) => s.participants.items);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState({ status: '', group: '', checkin: '' });
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    dispatch(participantsWatchRequested(eventId));
  }, [dispatch, eventId]);

  const filtered = useMemo(
    () =>
      people.filter((p) => {
        if (q && !`${p.name}${p.email}${p.company}`.toLowerCase().includes(q.toLowerCase())) {
          return false;
        }
        if (filter.status && p.status !== filter.status) return false;
        if (filter.group && !p.tags.includes(filter.group)) return false;
        if (filter.checkin === 'checked' && !p.checkedIn) return false;
        if (filter.checkin === 'not' && p.checkedIn) return false;
        return true;
      }),
    [people, q, filter],
  );

  const stats = useMemo(() => {
    const total = people.length;
    const confirmed = people.filter((p) => p.status === 'confirmed').length;
    const checked = people.filter((p) => p.checkedIn).length;
    return { total, confirmed, checked, rate: total ? Math.round((checked * 100) / total) : 0 };
  }, [people]);

  return (
    <>
      <div className="page-head">
        <div>
          <h2>{t('eo.nav.participants')}</h2>
          <p>
            {stats.total} {t('eo.participants.total')} · {stats.confirmed} {t('eo.participants.confirmed')} ·{' '}
            {stats.rate}% {t('eo.participants.attendance')}
          </p>
        </div>
        <div className="page-head-actions">
          <button type="button" className="btn btn-outline" onClick={() => navigate(`/eo/events/${eventId}/participants/import`)}>
            <EOIcon n="upload" size={15} />
            {t('eo.participants.import')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setAddOpen(true)}>
            <EOIcon n="plus" size={15} />
            {t('eo.participants.add')}
          </button>
        </div>
      </div>

      <div className="stat-row">
        <div className="stat">
          <p className="lbl">{t('eo.participants.statTotal')}</p>
          <p className="val">{stats.total}</p>
        </div>
        <div className="stat">
          <p className="lbl">{t('eo.participants.statConfirmed')}</p>
          <p className="val">{stats.confirmed}</p>
        </div>
        <div className="stat">
          <p className="lbl">{t('eo.participants.statPending')}</p>
          <p className="val">{people.filter((p) => p.status === 'pending').length}</p>
        </div>
        <div className="stat">
          <p className="lbl">{t('eo.participants.statAttendance')}</p>
          <p className="val">{stats.rate}%</p>
        </div>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search">
            <EOIcon n="search" size={16} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('eo.participants.search')} />
          </div>
          <select className="select" style={{ maxWidth: 160 }} value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
            <option value="">{t('eo.participants.allStatuses')}</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Declined</option>
          </select>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('eo.participants.colName')}</th>
                <th>{t('eo.participants.colCompany')}</th>
                <th>{t('eo.participants.colStatus')}</th>
                <th>{t('eo.participants.colCheckin')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="name">
                      <EOAvatar name={p.name} />
                      <div>
                        <strong>{p.name}</strong>
                        <small>{p.email}</small>
                      </div>
                    </div>
                  </td>
                  <td>{p.company}</td>
                  <td>
                    <span className={`badge ${p.status === 'confirmed' ? 'sage' : p.status === 'pending' ? 'clay' : 'danger'} dot`}>
                      {p.status}
                    </span>
                  </td>
                  <td>
                    {p.checkedIn ? (
                      <span className="badge sage dot">{t('eo.participants.checkedIn')}</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => dispatch(participantCheckInRequested({ eventId, participantId: p.id }))}
                      >
                        <EOIcon n="check" size={12} />
                        {t('eo.participants.checkIn')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {addOpen ? (
        <AddParticipantModal
          onClose={() => setAddOpen(false)}
          onSave={(input) => {
            dispatch(participantCreateRequested({ eventId, input }));
            setAddOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function AddParticipantModal({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (input: { name: string; email: string; company?: string; tags?: string[] }) => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  return (
    <EOModal
      open
      title={t('eo.participants.add')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => onSave({ name, email, company, tags: ['General'] })}>
            {t('common.submit')}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div className="field">
          <label>{t('eo.participants.name')}</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label>{t('eo.participants.email')}</label>
          <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label>{t('eo.participants.company')}</label>
          <input className="input" value={company} onChange={(e) => setCompany(e.target.value)} />
        </div>
      </div>
    </EOModal>
  );
}
